import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { AppRoute } from '@common/app/routes';
import { StringXSmall } from '@components/Typography/Typography';
import cn from 'classnames';
import { useTimeout } from 'usehooks-ts';

import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { calcTimeToEndFromNow } from '@/common/lib/date';
import { milisecondsToTime } from '@/common/lib/timer';
import { type LotteryTicketType } from '@/common/types/lottery';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { LeagueTimer } from '@/components/ui/Timer/LeagueTimer';
import { selectShortLotteryInfoByTier } from '@/redux/selectors/userSelectors';
import { useAppSelector } from '@/redux/store';

import { NavigationIcon } from './NavigationIcon';

import css from './styles.module.scss';

import sivlerTicketPng from '/img/icons/ticket-silver.png';
import sivlerTicketWebp from '/img/icons/ticket-silver.webp';

interface LotteryNavigationItemProps {
  animationStep?: 1 | 2 | 3;
  className?: string;
}

const LotteryNavigationItem: FC<LotteryNavigationItemProps> = ({
  animationStep,
  className,
}) => {
  const { t } = useTranslation();
  const [isTimerEnd, setIsTimerEnd] = useState(false);
  const isLotteryHappeningNow = useAppSelector(
    (state) => state.lottery.isLotteryHappeningNow,
  );
  const [currentTierName, setCurrentTierName] =
    useState<LotteryTicketType>('Silver');
  const baseCurrentLotteryInfo = useAppSelector((state) =>
    selectShortLotteryInfoByTier(state, currentTierName),
  );
  const msToTimerEnd = useMemo(
    () => calcTimeToEndFromNow(baseCurrentLotteryInfo?.finishedAt || 0),
    [baseCurrentLotteryInfo?.finishedAt],
  );
  const secondsToEnd = useCountDownTimer(msToTimerEnd);
  const label = 'Tickets';

  useTimeout(
    () => {
      setIsTimerEnd(true);
      setCurrentTierName((state) => (state === 'Silver' ? 'Gold' : 'Silver'));
    },
    baseCurrentLotteryInfo?.finishedAt ? msToTimerEnd : null,
  );

  useEffect(() => {
    if (isLotteryHappeningNow !== undefined && !isLotteryHappeningNow) {
      setIsTimerEnd(false);
    }
  }, [isLotteryHappeningNow]);

  const lotteryObjectTime = milisecondsToTime(secondsToEnd);

  return (
    <NavLink to={AppRoute.LOTTERY} className={className}>
      <div className={css.navItem}>
        <div className="navigationIcon relative">
          <NavigationIcon name="tickets" />
          {animationStep === 2 && (
            <MotionPicture
              layoutId="contract-complete-silver-ticket"
              transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
              img={sivlerTicketPng}
              webp={sivlerTicketWebp}
              className="w-[48px] h-[48px] rounded-full block absolute left-[8px] top-[18px]"
              style={{ zIndex: -2 }}
            />
          )}
        </div>
        <StringXSmall
          weight="medium"
          className={cn(
            'navigationText relative text-nowrap whitespace-nowrap',
          )}
        >
          {isTimerEnd || isLotteryHappeningNow ? (
            t(label)
          ) : (
            <LeagueTimer value={lotteryObjectTime} text={undefined} />
          )}
        </StringXSmall>
      </div>
    </NavLink>
  );
};

export { LotteryNavigationItem };
